import { useEffect } from "react";
import "styles/modalBackground.css";

const MODAL_SELECTOR = [
  '.MuiModal-root:not(.MuiModal-hidden):not(.MuiPopover-root):not(.MuiDrawer-root)',
  '[role="dialog"][aria-modal="true"]',
  '[role="alertdialog"][aria-modal="true"]',
  'dialog[open]',
  '.quotation-dialog-scrim',
  '.contacts-modal-scrim',
  '.contact-modal-overlay',
  '.duplicate-modal-overlay',
  '.document-preview-overlay',
  '.swal-overlay--show-modal',
  '.swal2-container:has(.swal2-popup:not(.swal2-toast))',
].join(", ");

const NAVIGATION_SELECTOR = [
  ".dashboard-nav",
  ".dashboard-overlay",
  ".dashboard-sidebar-launcher",
  ".app-topbar",
  ".MuiDrawer-docked",
].join(", ");
const RCTI_WORKSPACE_SELECTOR =
  ".rcti-review-workspace, .rcti-invoice-preview-backdrop";
const RCTI_WORKSPACE_DIALOG_SELECTOR =
  ".rcti-communication-dialog, .rcti-invoice-preview-dialog";
const RCTI_WORKSPACE_NAVIGATION_SELECTOR = [
  ".dashboard-nav",
  ".dashboard-overlay",
  ".dashboard-sidebar-launcher",
  ".app-topbar",
].join(", ");

export default function useModalBackgroundLock() {
  useEffect(() => {
    const root = document.documentElement;
    const navigationState = new Map();
    let activeModals = [];
    let rctiWorkspaceOnly = false;
    let frame = null;

    const restoreNavigation = () => {
      navigationState.forEach((wasInert, element) => {
        if (!wasInert) element.removeAttribute("inert");
      });
      navigationState.clear();
    };

    const syncModals = () => {
      frame = null;
      activeModals = Array.from(document.querySelectorAll(MODAL_SELECTOR)).filter(
        (element) =>
          !element.closest(".MuiModal-hidden, .MuiPopover-root, .MuiDrawer-root") &&
          element.getClientRects().length > 0 &&
          window.getComputedStyle(element).visibility !== "hidden"
      );
      root.toggleAttribute("data-modal-background-locked", activeModals.length > 0);
      rctiWorkspaceOnly =
        activeModals.length > 0 &&
        activeModals.every(
          (modal) =>
            modal.matches(RCTI_WORKSPACE_DIALOG_SELECTOR) &&
            Boolean(modal.closest(RCTI_WORKSPACE_SELECTOR))
        );
      root.toggleAttribute("data-rcti-workspace-open", rctiWorkspaceOnly);

      if (!activeModals.length) {
        restoreNavigation();
        return;
      }

      document.querySelectorAll(NAVIGATION_SELECTOR).forEach((element) => {
        if (
          rctiWorkspaceOnly &&
          element.matches(RCTI_WORKSPACE_NAVIGATION_SELECTOR)
        ) {
          if (navigationState.get(element) === false) {
            element.removeAttribute("inert");
            navigationState.delete(element);
          }
          return;
        }
        if (!navigationState.has(element)) {
          navigationState.set(element, element.hasAttribute("inert"));
          element.setAttribute("inert", "");
        }
      });
    };

    const scheduleSync = () => {
      if (frame === null) frame = window.requestAnimationFrame(syncModals);
    };
    const preventBackgroundScroll = (event) => {
      const targetElement = event.target instanceof Element ? event.target : null;
      const popupControl = targetElement?.closest(
        ".MuiPopover-root, .MuiPopper-root, .react-datepicker-popper, .react-datepicker__portal"
      );
      const workspaceNavigation =
        rctiWorkspaceOnly &&
        targetElement?.closest(RCTI_WORKSPACE_NAVIGATION_SELECTOR);
      if (
        activeModals.length &&
        !popupControl &&
        !workspaceNavigation &&
        !activeModals.some((modal) => modal.contains(event.target))
      ) {
        event.preventDefault();
      }
    };

    const observer = new MutationObserver(scheduleSync);
    syncModals();
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "hidden", "aria-hidden", "aria-modal", "open"],
    });
    document.addEventListener("wheel", preventBackgroundScroll, { passive: false, capture: true });
    document.addEventListener("touchmove", preventBackgroundScroll, { passive: false, capture: true });

    return () => {
      observer.disconnect();
      if (frame !== null) window.cancelAnimationFrame(frame);
      document.removeEventListener("wheel", preventBackgroundScroll, true);
      document.removeEventListener("touchmove", preventBackgroundScroll, true);
      root.removeAttribute("data-modal-background-locked");
      root.removeAttribute("data-rcti-workspace-open");
      rctiWorkspaceOnly = false;
      restoreNavigation();
    };
  }, []);
}
